import { useHandleCancelableApi } from 'common/Hooks/UseHandleCancelableApi'
import { formatNumber, roundAtDecimals } from 'common/Utils/NumberUtilities'
import Modal from 'components/Common/Modal/Modal'
import PropTypes from 'prop-types'
import { useEffect, useMemo } from 'react'
import { withTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { Col, Row } from 'reactstrap'
import { getQuoteRegressionValues } from 'store/actions'
import CalculationMarginModal from './CalculationMarginModal'

const EditMarginsModal = ({
	isEditMarginsModalOpen,
	closeEditMarginsModal,
	marginModal,
	setMargin,
	setUnitCostByMargin,
	saveAndCloseMarginsModal,
	t,
	getQuoteRegressionValues,
	quoteRegressionValues
}) => {

	const newMargins = useMemo(() => marginModal && marginModal.quotes && marginModal.quotes.map(quote => (
		{ quantity: quote.quantity, value: roundAtDecimals(quote?.margin?.value, 2) || null }
	)), [marginModal])

	const getBody = () => {
		return { quoteId: marginModal?.quoteId, newMargins }
	}

	const { handleApiCall } = useHandleCancelableApi(getQuoteRegressionValues, getBody(), 500)

	useEffect(() => {
		if (newMargins && newMargins.every((margin) => margin.value != null)) {
			handleApiCall()
		}
	}, [newMargins])

	const quotes = marginModal?.quotes?.map(quote => ({
		...quote,
		quotation: {
			...quote.quotation,
			value: parseFloat(quote.quotation.value),
		},
	})) || []

	return (
		<Modal
			className="modal-xl"
			isOpen={isEditMarginsModalOpen}
			closeModal={closeEditMarginsModal}
			title={t('editMargins', { ns: 'naming' })}
			body={
				marginModal &&
					marginModal.quotes &&
					marginModal.quotes.length > 0 ? (
					<div className="modal-body">
						<Row className="font-12-px">
							<Col className="col-2">
								<span className="w-100 bottom-text">
									{t('quantity', { ns: 'naming' })}
								</span>
							</Col>
							<Col className="col-2">
								<span className="w-100 bottom-text">
									{t('unitBuying', { ns: 'naming' })}
								</span>
							</Col>
							<Col className="col-2">
								<span className="w-100 bottom-text">
									{t('unitBuyReal', { ns: 'naming' })}
								</span>
							</Col>
							<Col className="col-2">
								<span className="w-100 bottom-text">
									{t('marginPercentage', { ns: 'naming' })}
								</span>
							</Col>
							<Col className="col-2">
								<span className="w-100 bottom-text">
									{t('unitSelling', { ns: 'naming' })}
								</span>
							</Col>
							<Col className="col-2">
								<span className="w-100 bottom-text">
									{t('unitSaleReal', { ns: 'naming' })}
								</span>
							</Col>
						</Row>
						{marginModal &&
							marginModal.quotes.length > 0 &&
							marginModal.quotes.map((quote, index) => {
								const regressionValues = quoteRegressionValues?.find((regressionValue) => regressionValue.quantity == quote.quantity)
								return (
									<Row key={quote.quantity}>
										<Col className="col-2">
											<input
												id={'Quantity' + index + 'Input'}
												className="w-100 mb-3"
												disabled
												value={quote.quantity || ''}
											/>
										</Col>
										<Col className="col-2">
											<input
												id={'UnitBuying' + index + 'Input'}
												className="w-100 mb-3"
												disabled
												value={formatNumber(roundAtDecimals(quotes[index]?.quotation?.value, 2) || '')}
											/>
										</Col>
										<Col className="col-2">
											<input
												id={'unitBuyReal' + index + 'Input'}
												className="w-100 mb-3"
												disabled
												value={formatNumber(roundAtDecimals(regressionValues?.unitCost, 2) || '-')}
											/>
										</Col>
										<Col className="col-2">
											<input
												id={'marginPercentage' + index + 'Input'}
												type="number"
												className="w-100 mb-3"
												value={roundAtDecimals(quote?.margin?.value, 2) || ''}
												onChange={e =>
													setMargin(
														e.target.valueAsNumber,
														marginModal,
														index,
													)
												}
											/>
										</Col>
										<Col className="col-2">
											<input
												id={'unitSelling' + index + 'Input'}
												type="number"
												className="w-100 mb-3"
												value={roundAtDecimals(quote?.unitSell?.value, 2) || ''}
												onChange={e =>
													setUnitCostByMargin(
														e.target.valueAsNumber,
														index,
													)
												}
											/>
										</Col>
										<Col className="col-2">
											<input
												id={'unitSaleReal' + index + 'Input'}
												className="w-100 mb-3"
												disabled
												value={formatNumber(roundAtDecimals(regressionValues?.unitPrice, 2) || '-')}
											/>
										</Col>
									</Row>
								)
							},
							)

						}

						<CalculationMarginModal quoteId={marginModal.quoteId} newMargins={newMargins} />
					</div>
				) : (
					<div className="modal-body">
						{t('quotationNotAvailable', { ns: 'errors' })}
					</div>
				)
			}
			buttons={
				(marginModal &&
					marginModal.quotes &&
					marginModal.quotes.length > 0 && [
						<button
							id="SubmitEditMarginModalButton"
							type="button"
							key="btn3"
							className="btn btn-primary"
							onClick={() =>
								saveAndCloseMarginsModal(
									marginModal.partId.value,
									marginModal.supplierId,
									marginModal.quotes,
								)
							}
						>
							{t('submit', { ns: 'naming' })}
						</button>,
					]) || []
			}
		/>
	)
}

EditMarginsModal.propTypes = {
	isEditMarginsModalOpen: PropTypes.bool,
	closeEditMarginsModal: PropTypes.func,
	marginModal: PropTypes.object,
	setMargin: PropTypes.func,
	setUnitCostByMargin: PropTypes.func,
	saveAndCloseMarginsModal: PropTypes.func,
	t: PropTypes.func,
	getQuoteRegressionValues: PropTypes.func,
	quoteRegressionValues: PropTypes.object
}

const mapStateToProps = (state) => ({
	quoteRegressionValues: state.Quotes.quoteRegressionValues,
})

const mapDispatchToProps = { getQuoteRegressionValues }

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(EditMarginsModal))
